/**
 * @file hooks.ts
 *
 * @description This file defines custom hooks to be used within the YourSims and children component.
 * We are defining in a separate file for easy mocking in unit tests.
 */
import { useContext } from 'react';
import YourSimsContext from './context';
import ModalsProviderContext from './_components/ModalsProvider/ModalsProvider';
import { SimRowContext } from './_components/SimListRow/SimListRow';

/**
 * Custom hook to access the YourSimsContext
 *
 * @throws {Error} If used outside of YourSimsContextProvider
 *
 * @returns The your sims context value
 */
export function useYourSimsContext() {
  const context = useContext(YourSimsContext);

  if (!context)
    throw new Error(
      'useYourSimsContext must be used within a YourSimsContextProvider'
    );

  return context;
}

/**
 * Custom hook to access the SimRowContext
 *
 * @throws {Error} If used outside of SimRowContext.Provider
 *
 * @returns The sim row context value
 */
export function useSimRowContext() {
  const context = useContext(SimRowContext);

  if (context === null)
    throw new Error(
      'useSimRowContext must be used within a SimRowContext.Provider'
    );

  return context;
}

/**
 * Custom hook to access the ModalsProviderContext
 *
 * @throws {Error} If used outside of ModalsProvider
 *
 * @returns The modals context value
 */
export function useModalsContext() {
  const context = useContext(ModalsProviderContext);

  if (!context)
    throw new Error('useModalsContext must be used within a ModalsProvider');

  return context;
}
